import React from 'react';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Box, Container, Typography, IconButton } from '@mui/material';
import { store, type RootState } from './app/store';
import { arbitreasureTheme } from './theme';
import { ConnectionStatus } from './components/ConnectionStatus/ConnectionStatus';
import { HeaderStats } from './components/HeaderStats';
import OpportunityTable from './components/OpportunityTable/OpportunityTable';
import SettingsDialog from './components/SettingsDialog/SettingsDialog';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { FuturesDataDebugStatus } from './components/FuturesDataDebugStatus';
import { useWebSocketConnection } from './hooks/useWebSocketConnection';
import { OpportunitiesProvider, useOpportunities } from './contexts/OpportunitiesContext';
import { FuturesDataProvider } from './contexts/FuturesDataContext';
import { 
  openSettingsDialog, 
  closeSettingsDialog, 
  updateSettings, 
  resetSettings 
} from './features/settings/settingsSlice';
import { selectNetworkStatus } from './features/networkStatus/networkStatusSlice';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { 
  isOpportunityTransferrable, 
  hasNetworkStatusData,
  getArbitrageTransferStatus 
} from './utils/networkCompatibility';
import { FeeStructure } from './models/FeeStructure';
import { convertVolumeToUSDT } from './utils/currencyConversion';
import ExchangeNetworkStatusTest from './ExchangeNetworkStatusTest';
import OrderbookPage from './features/orderbook/OrderbookPage';
import FuturesSpotPage from './pages/FuturesSpotPage';
import FuturesSpotPageMinimal from './pages/FuturesSpotPageMinimal';
import FuturesSymbolDetailPage from './pages/FuturesSymbolDetailPage';
import TraderDashboardPageLegacy from './pages/TraderDashboardPageLegacy';
import TraderDashboardPage from './pages/TraderDashboardPage';
import TraderOpportunitiesPage from './pages/TraderOpportunitiesPage';
import TestCcxtWebSocketPage from './pages/TestCcxtWebSocketPage';
import BookTickerTestPage from './pages/BookTickerTestPage';
import { TraderNameProvider } from './contexts/TraderNameContext';
import { MarketWarningsProvider } from './contexts/MarketWarningsContext';
import './App.css';

function AppContent() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { settings, isDialogOpen } = useSelector((state: RootState) => state.settings);

  // Check if we're in test mode
  const urlParams = new URLSearchParams(window.location.search);
  const isExchangeTest = urlParams.get('test') === 'exchange';
  const isAnyTest = isExchangeTest;

  // Only use WebSocket connection if not in test mode AND we're on the main dashboard
  // This component only renders on '/' route, so WebSocket will only be active there
  const { isConnected } = isAnyTest ? { isConnected: false } : useWebSocketConnection();
  
  // Get opportunities from Context (populated by WebSocket) - NOT Redux anymore!
  const { sortedOpportunities: allOpportunities } = useOpportunities();
  
  // Get network status for transfer filtering
  const networkStatus = useSelector(selectNetworkStatus);
  const { isAnyStale } = isAnyTest ? { isAnyStale: false } : useNetworkStatus(); // This also loads initial data
  
  // Update fee structure with custom overrides when settings change
  React.useEffect(() => {
    FeeStructure.setCustomFees(settings.feeOverrides);
  }, [settings.feeOverrides]);
  
  const filteredOpportunities = allOpportunities.filter(opp => {
    // ALWAYS keep starred items regardless of filters
    if (opp.isStarred) {
      return true;
    }
    
    // Filter by excluded tokens
    if (settings.filters.excludedTokens.includes(opp.base)) {
      return false;
    }
    
    // Filter by minimum profit percentage
    const profitPercent = ((opp.profit_ratio || 1.0) - 1) * 100;
    if (profitPercent < settings.filters.minProfitPercent) {
      return false;
    }
    
    // Filter by maximum profit percentage (if enabled)
    if (settings.filters.maxProfitPercent !== null && profitPercent > settings.filters.maxProfitPercent) {
      return false;
    }
    
    // Filter by minimum volume USD - convert to USDT for consistent comparison
    // Use buy side volume and price for the calculation
    const volumeInQuote = (opp.max_volume || 0) * (opp.buy_price || 0);
    const volumeUSDT = convertVolumeToUSDT(volumeInQuote, opp.buy_quote);
    if (volumeUSDT < settings.filters.minVolumeUSD) {
      return false;
    }
    
    // Filter by enabled exchanges (using separate buy/sell filters)
    // Default to true if not specified (exchange is enabled by default)
    const buyEnabled = settings.buyExchanges?.[opp.buy_exchange] ?? true;
    const sellEnabled = settings.sellExchanges?.[opp.sell_exchange] ?? true;
    
    // Filter out if either exchange is disabled
    if (!buyEnabled || !sellEnabled) {
      return false;
    }
    
    // Filter by network status (if enabled)
    if (settings.filters.networkStatus?.enabled && hasNetworkStatusData(networkStatus)) {
      const transferStatus = getArbitrageTransferStatus(opp, networkStatus);
      
      // Check if we should hide unknown status
      if (settings.filters.networkStatus.hideUnknown) {
        if (transferStatus.withdrawStatus === 'unknown' || transferStatus.depositStatus === 'unknown') {
          return false;
        }
      }
      
      // Check if we should hide disabled (guaranteed not transferrable)
      if (settings.filters.networkStatus.hideDisabled) {
        if (transferStatus.withdrawStatus === 'disabled' || transferStatus.depositStatus === 'disabled') {
          return false;
        }
      }
      
      // Check if both must be enabled (strict mode - only show guaranteed transferrable)
      if (settings.filters.networkStatus.requireBothEnabled) {
        if (transferStatus.withdrawStatus !== 'enabled' || transferStatus.depositStatus !== 'enabled') {
          return false;
        }
      }
    }
    
    return true;
  });
  
  
  // Sort opportunities based on settings, but keep starred items at the top
  const sortedOpportunities = [...filteredOpportunities].sort((a, b) => {
    // ALWAYS keep starred items at the top
    if (a.isStarred && !b.isStarred) return -1;
    if (!a.isStarred && b.isStarred) return 1;
    
    // Within each group (starred or non-starred), apply the user's sort preference
    switch (settings.display.sortBy) {
      case 'profit_desc':
        return (b.profit_ratio || 1.0) - (a.profit_ratio || 1.0);
      case 'created_desc':
        return b.detected_at - a.detected_at;
      case 'updated_desc':
        return b.updated_at - a.updated_at;
      default:
        return 0;
    }
  });

  const handleOpenSettings = React.useCallback(() => {
    dispatch(openSettingsDialog());
  }, [dispatch]);

  const handleCloseSettings = React.useCallback(() => {
    dispatch(closeSettingsDialog());
  }, [dispatch]);

  const handleApplySettings = React.useCallback((newSettings: typeof settings) => {
    dispatch(updateSettings(newSettings));
  }, [dispatch]);

  const handleResetSettings = React.useCallback(() => {
    dispatch(resetSettings());
  }, [dispatch]);
  
  if (isExchangeTest) {
    return <ExchangeNetworkStatusTest />;
  }

  return (
    <TraderNameProvider traderName="njovy">
      {/* Settings Dialog */}
      <SettingsDialog
        open={isDialogOpen}
        onClose={handleCloseSettings}
        currentSettings={settings}
        onApply={handleApplySettings}
        onReset={handleResetSettings}
      />

      {/* Settings Button - Fixed Bottom Right */}
      <IconButton
        onClick={handleOpenSettings}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24, md: 32 },
          right: { xs: 16, sm: 24, md: 32 },
          color: 'primary.main',
          border: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'rgba(0, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          zIndex: 1000,
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 255, 0.2)',
            boxShadow: '0 0 30px rgba(0, 255, 255, 0.6)',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label={t('app.settings')}
      >
        ⚙️
      </IconButton>

      {/* Language Switcher */}
      <LanguageSwitcher />

      {/* WebSocket Connection Status */}
      <ConnectionStatus />

      {/* DEBUG: Futures Data Status - Remove after debugging */}
      {/*<FuturesDataDebugStatus />*/}
      
      {/* Main Application */}
      <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #020817 0%, #0a0f1e 100%)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 40px,
                  rgba(0, 255, 255, 0.03) 40px,
                  rgba(0, 255, 255, 0.03) 41px
                ),
                repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent 40px,
                  rgba(255, 0, 255, 0.03) 40px,
                  rgba(255, 0, 255, 0.03) 41px
                )
              `,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.05) 0%, transparent 50%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ 
            pt: { xs: 8, sm: 6, md: 4 }, 
            pb: { xs: 2, sm: 3, md: 4 }, 
            px: 0,
            position: 'relative', 
            zIndex: 1,
            width: '100%'
          }}>
            {/* Header */}
            <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2.75rem', md: '3.75rem' },
                  fontWeight: 'bold',
                  letterSpacing: { xs: '0.05em', sm: '0.1em' },
                  textTransform: 'uppercase',
                  background: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 50%, #00ffff 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 40px rgba(0, 255, 255, 0.8)',
                  animation: 'gradient 3s ease 10',
                  '@keyframes gradient': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                  mb: 1,
                }}
              >
                {t('app.title').toUpperCase()}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.875rem', md: '1rem' },
                  color: 'text.secondary',
                  letterSpacing: { xs: '0.05em', sm: '0.2em' },
                  textTransform: 'uppercase',
                  px: { xs: 2, sm: 0 },
                }}
              >
                {t('app.subtitle')}
              </Typography>
            </Box>

            {/* Header Statistics Bar - Story 4.6 Implementation */}
            <HeaderStats />

            {/* Dashboard Content */}
            {/* Opportunity Table - Story 4.2 Implementation */}
            <OpportunityTable 
              opportunities={sortedOpportunities}
              loading={!isConnected}
              networkStatusStale={isAnyStale}
              networkStatusAvailable={hasNetworkStatusData(networkStatus)}
            />
          </Box>
        </Box>
    </TraderNameProvider>
  );
}

function AppWithProviders() {
  // Access settings to pass futures enabled flag - must be inside Provider!
  const { settings } = useSelector((state: RootState) => state.settings);
  const location = useLocation();

  // Keep futures WebSocket enabled globally (used by main dashboard and /futures page)
  // Only respect the user's settings flag, don't disable based on route
  // The FuturesDataProvider wraps all routes, so it should stay connected
  const futuresEnabled = settings.features?.enableFutures ?? true;

  // DEBUG: Log futures enabled state
  console.log('[App] Futures enabled:', futuresEnabled, '| Path:', location.pathname, '| Settings:', settings.features?.enableFutures);

  return (
    <FuturesDataProvider enabled={futuresEnabled}>
      <MarketWarningsProvider>
        <OpportunitiesProvider>
          <ThemeProvider theme={arbitreasureTheme}>
            <CssBaseline />
            <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/user/:trader_name" element={<TraderDashboardPage />} />
            <Route path="/user/:trader_name/v1" element={<TraderDashboardPageLegacy />} />
            <Route path="/user/:trader_name/v2" element={<TraderDashboardPage />} />
            <Route path="/user/:trader_name/opportunities" element={<TraderOpportunitiesPage />} />
            <Route path="/user/:trader_name/v1/opportunities" element={<TraderOpportunitiesPage />} />
            <Route path="/user/:trader_name/v2/opportunities" element={<TraderOpportunitiesPage />} />
            <Route path="/futures/:symbol" element={<FuturesSymbolDetailPage />} />
            <Route path="/futures" element={<FuturesSpotPage />} />
            <Route path="/orderbook/:exchange/:symbol" element={<OrderbookPage />} />
            <Route path="/orderbook/:exchange/:base/:quote" element={<OrderbookPage />} />
            <Route path="/orderbooks/:exchange1/:symbol1/:exchange2/:symbol2" element={<OrderbookPage />} />
            <Route path="/orderbooks/:exchange1/:base1/:quote1/:exchange2/:base2/:quote2" element={<OrderbookPage />} />
            <Route path="/f/:exchange/:symbol" element={<OrderbookPage isFutures={true} />} />
            <Route path="/test-ccxt-ws" element={<TestCcxtWebSocketPage />} />
            <Route path="/test-bookticker" element={<BookTickerTestPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
        </OpportunitiesProvider>
      </MarketWarningsProvider>
    </FuturesDataProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppWithProviders />
    </Provider>
  );
}

export default App;
